'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { LinkIcon, PaperclipIcon, XIcon } from 'lucide-react'
import React from 'react'

import { useViewport } from '@/contexts/viewportProvider'
import { useForm } from '@/hooks/useForm'
import { cn } from '@/lib/cn'
import type { FileType } from '@/types/fileType'
import { Button, type ButtonProps } from '../button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogSeparator,
  DialogTitle,
} from '../dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../form'
import { Input } from '../input'
import { MentionTextarea } from '../mentionTextarea'
import type { RichTextEditorProps } from '../richTextEditor'
import { useToast } from '../toast'
import { type InputCommentLinkFormData, inputCommentLinkSchema } from './schema'

type InputCommentContextProps = {
  value?: string
  handleChangeValue: (value: string) => void
  attachment?: FileType
  handleChangeAttachment: (attachment?: FileType) => void
}

const InputCommentContext = React.createContext({} as InputCommentContextProps)

type InputCommentProps = React.HTMLAttributes<HTMLDivElement> & {
  value?: string
  onChangeValue: (value: string) => void
  attachment?: FileType
  onChangeAttachment: (attachment?: FileType) => void
}
const InputComment = React.forwardRef<HTMLDivElement, InputCommentProps>(
  (
    {
      className,
      value,
      onChangeValue,
      attachment,
      onChangeAttachment,
      ...props
    },
    ref,
  ) => {
    const handleChangeValue = React.useCallback(
      (value: string) => {
        onChangeValue(value)
      },
      [onChangeValue],
    )

    const handleChangeAttachment = React.useCallback(
      (attachment?: FileType) => {
        onChangeAttachment(attachment)
      },
      [onChangeAttachment],
    )

    const providerValues = React.useMemo(
      () => ({
        value,
        handleChangeValue,
        attachment,
        handleChangeAttachment,
      }),
      [value, handleChangeValue, attachment, handleChangeAttachment],
    )

    return (
      <InputCommentContext.Provider value={providerValues}>
        <div
          ref={ref}
          className={cn(
            'rounded-sm border border-system-border-primary bg-white w-full',
            className,
          )}
          {...props}
        />
      </InputCommentContext.Provider>
    )
  },
)
InputComment.displayName = 'InputComment'

type InputCommentEditorProps = Omit<RichTextEditorProps, 'value' | 'onChange'>

const InputCommentEditor = ({
  placeholder,
  isError,
  className,
}: InputCommentEditorProps) => {
  const { value, handleChangeValue } = useInputComment()
  return (
    <MentionTextarea
      value={value || ''}
      onChange={handleChangeValue}
      placeholder={placeholder}
      isError={isError}
      className={cn('mt-px w-full border-none', className)}
    />
  )
}
InputCommentEditor.displayName = 'InputCommentEditor'

const InputCommentFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-between py-2 px-4 border-t border-system-border-primary bg-white',
        className,
      )}
      {...props}
    />
  )
})
InputCommentFooter.displayName = 'InputCommentFooter'

type InputCommentAttachmentProps = ButtonProps & {
  mimeType?: string[]
  maxSizeInMB?: number
}
const InputCommentAttachment = React.forwardRef<
  HTMLButtonElement,
  InputCommentAttachmentProps
>(({ className, mimeType, maxSizeInMB = 3, children, ...props }, ref) => {
  const { attachment, handleChangeAttachment } = useInputComment()

  const { toast } = useToast()
  const { isMobile } = useViewport()

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleFileInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target

      if (!files) {
        toast({
          title: 'Nenhum arquivo selecionado',
          variant: 'destructive',
        })
        return
      }

      const [file] = Array.from(files)
      const maxSize = maxSizeInMB * 1024 * 1024

      if (file.size > maxSize) {
        toast({
          title: `O arquivo deve ter no máximo ${maxSizeInMB}MB`,
          variant: 'destructive',
        })
        return
      }

      if (!mimeType) {
        handleChangeAttachment({ file })
        return
      }

      const isValidMimeType = mimeType?.some((type) => {
        if (type.endsWith('/*')) {
          const [baseType] = type.split('/')
          return file.type.startsWith(baseType)
        }
        return file.type === type
      })

      if (!isValidMimeType) {
        toast({
          title: 'Tipo de arquivo inválido',
          variant: 'destructive',
        })
        return
      }

      handleChangeAttachment({ file })
    },
    [handleChangeAttachment, maxSizeInMB, mimeType, toast],
  )

  const handleRemoveAttachment = React.useCallback(() => {
    handleChangeAttachment()
  }, [handleChangeAttachment])

  if (attachment?.file) {
    return (
      <div className="flex items-center gap-2">
        <PaperclipIcon className="size-5 text-system-primary-icon" />
        <div className="flex items-center gap-1">
          <span className="text-sm text-system-text-primary">
            {attachment.file.name}
          </span>
          <Button
            type="button"
            className="text-system-text-primary"
            variant="ghost"
            size="icon"
            onClick={handleRemoveAttachment}
          >
            <XIcon className="size-5 text-system-state-error" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Button
      ref={ref}
      type="button"
      className={cn('text-system-text-primary', className)}
      variant="ghost"
      onClick={() => fileInputRef.current?.click()}
      {...props}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={mimeType?.join(', ')}
        onChange={handleFileInputChange}
      />

      <PaperclipIcon className="size-5 text-system-primary-icon" />
      {!isMobile && children}
    </Button>
  )
})
InputCommentAttachment.displayName = 'InputCommentAttachment'

type InputCommentLinkProps = ButtonProps
const InputCommentLink = React.forwardRef<
  HTMLButtonElement,
  InputCommentLinkProps
>(({ className, children, ...props }, ref) => {
  const [isOpenLinkModal, setIsOpenLinkModal] = React.useState(false)
  const { isMobile } = useViewport()

  return (
    <>
      <Button
        ref={ref}
        type="button"
        className={cn('text-system-text-primary', className)}
        variant="ghost"
        onClick={() => setIsOpenLinkModal(true)}
        {...props}
      >
        <LinkIcon className="size-5 text-system-primary-icon" />
        {!isMobile && children}
      </Button>

      {isOpenLinkModal && (
        <InputCommentLinkModal
          isOpen={isOpenLinkModal}
          onClose={() => setIsOpenLinkModal(false)}
        />
      )}
    </>
  )
})
InputCommentLink.displayName = 'InputCommentLink'

type InputCommentLinkModalProps = {
  isOpen: boolean
  onClose: () => void
}
const InputCommentLinkModal = ({
  isOpen,
  onClose,
}: InputCommentLinkModalProps) => {
  const { value, handleChangeValue } = useInputComment()

  const form = useForm<InputCommentLinkFormData>({
    defaultValues: {
      link: '',
    },
    resolver: zodResolver(inputCommentLinkSchema),
  })

  const onSubmit = React.useCallback(
    (data: InputCommentLinkFormData) => {
      const linkHtml = `<a href="${data.link}" target="_blank">${data.link}</a>`

      if (!value) {
        handleChangeValue(linkHtml)
        onClose()
        return
      }

      const valueWithLinks = `${value} ${linkHtml}`
      handleChangeValue(valueWithLinks)
      onClose()
    },
    [handleChangeValue, onClose, value],
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent role="dialog">
        <DialogHeader>
          <DialogTitle>Adicionar link</DialogTitle>
          <DialogDescription>Adicione links externos</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <FormField
            control={form.control}
            name="link"
            render={({ field, fieldState }) => {
              const hasError = fieldState.error?.message
              return (
                <FormItem>
                  <FormLabel>Link*</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://"
                      variant={hasError ? 'error' : 'default'}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )
            }}
          />
        </Form>

        <DialogSeparator />

        <DialogFooter>
          <Button type="button" onClick={onClose} variant="ghost">
            Cancelar
          </Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            type="button"
            variant="accent"
          >
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
InputCommentLinkModal.displayName = 'InputCommentLinkModal'

const useInputComment = () => {
  const context = React.useContext(InputCommentContext)

  if (!context) {
    throw new Error('useInputComment must be used within an <InputComment>')
  }

  return context
}

export {
  InputComment,
  InputCommentEditor,
  InputCommentFooter,
  InputCommentAttachment,
  InputCommentLink,
}
